import React, { useCallback, useMemo, useEffect, useState } from "react";
import { tap, switchMap, map, of, catchError } from "rxjs";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import isLoggedIn from "../../../../common/helpers/isUserLoggedIn";
import apiClient from "../../../../services/apiClient";
import isUserProfilePublic from "../../../../common/helpers/isUserProfilePublic";
import ThumbsUpIcon from "../../../icons/ThumbUp";
import AddToast from "../../../../state/actions/AddToast";
import SourceEntity from "../../models/SourceEntity";
import store from "../../../../state/store";
import threadService from "../../../../services/threadService";
import Topic from "../../../../models/Topic";
import { getThreadSelector } from "../../../../state/selectors/general.selectors";
import Btn from "../../../atoms/Btn";
import isPortal from "../../../../common/helpers/isPortal";

function LikeButton({
  sourceEntity,
  topic,
  slug,
  subType,
  subTypeId,
  object,
  size,
}) {
  const isClinical = isPortal();
  const [isLoading, setIsLoading] = useState(false);
  const loggedOut = !isLoggedIn();
  const isNotPubl = !isUserProfilePublic();
  const cantLike = !topic.canUpvote;
  const isDisabled = loggedOut || isNotPubl || cantLike;
  const { type, id: typeId, description } = sourceEntity;

  const tooltipTxt = useMemo(() => {
    const obj = object ? `this ${object.replace(/s$/, "")}` : null;
    const sub = subType ? `the ${subType.replace(/s$/, "")}` : null;
    const entities = [obj, sub, description].filter(Boolean);
    return `Is ${entities[0]} helpful for ${entities[1]}?`;
  }, [object, subType, description]);

  const activTxt = isLoading ? "saving... " : tooltipTxt;
  const loggedOutMsg = "To contribute, please sign in";
  const disabledTxt = "You cannot perform this action.";
  const inactTxt = loggedOut ? loggedOutMsg : disabledTxt;
  const tooltipText = isDisabled ? inactTxt : activTxt;

  useEffect(() => {
    threadService.loadCommentStats(slug);
  }, [slug]);

  const toggle = useCallback(
    (origTopic) => {
      const updatedTopic = origTopic.clone();

      function updateStore(updated) {
        const { threadMap: current } = store.getState() || {};
        const threadMap = current.set(slug, updated);
        store.dispatch({ type: "SET_THREAD_MAP", threadMap });
      }

      function handleError(err) {
        const action = origTopic.isLiked ? "downvote" : "upvote";
        AddToast(`Failed to register ${action}`);
        console.error(err);
        return of(origTopic);
      }

      function sendRequest() {
        const action = "upvote";
        const data = { comment_id: updatedTopic.postId, action };
        return updatedTopic.isLiked
          ? apiClient.upvote(type, typeId, subType, subTypeId, data)
          : apiClient.downvote(type, typeId, subType, subTypeId, data);
      }

      of(null)
        .pipe(
          map(() => updatedTopic.toggleUpvote()),
          tap(updateStore),
          tap(() => setIsLoading(true)),
          switchMap(sendRequest),
          map((x) => updatedTopic.updateUpvoteData(x?.result || {}).clone()),
          catchError(handleError),
          tap(() => setIsLoading(false)),
          tap(updateStore)
        )
        .subscribe();
    },
    [type, subType, subType, subTypeId]
  );

  const cl = isDisabled
    ? "tw-opacity-70"
    : "tw-opacity-70 hover:tw-opacity-100";
  const activeClass = topic.isLiked ? "tw-text-blue-500" : cl;

  const colorClass = topic.isLoading
    ? "tw-opacity-40 tw-pointer-events-none tw-cursor-not-allowed"
    : activeClass;

  return isClinical ? null : (
    <div
      className={`tw-flex tw-items-center tw-relative tw-gap-2 ${colorClass}`}
    >
      <Btn
        disabled={isLoading || isDisabled}
        onSubmit={() => toggle(topic)}
        data-tooltip={tooltipText}
        data-tooltip-delay={700}
        data-tooltip-class="publications-tooltip"
        className="tw-flex tw-items-center"
      >
        <div className="tw-transition-colors tw-duration-300">
          <ThumbsUpIcon className="tw-fill-current" size={size} />
        </div>
      </Btn>
      <div>{topic.upvoteCount}</div>
    </div>
  );
}

LikeButton.propTypes = {
  sourceEntity: PropTypes.instanceOf(SourceEntity),
  subType: PropTypes.string,
  subTypeId: PropTypes.string,
  topic: PropTypes.instanceOf(Topic),
  slug: PropTypes.string,
  object: PropTypes.string,
  size: PropTypes.number,
};

LikeButton.defaultProps = {
  sourceEntity: null,
  subType: "",
  subTypeId: "",
  topic: new Topic({}),
  slug: "",
  object: "",
  size: 16,
};

const mapStateToProps = (state, { slug }) => {
  return {
    topic: getThreadSelector(state, slug),
  };
};

export default React.memo(connect(mapStateToProps, {})(LikeButton));
